import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from 'components/table/DataTable';
import { useModelService } from 'services/model/model.service';
import { useIntl } from 'react-intl';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { useEntriesDemandReportData } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/queryHooks';
import { useContext, useEffect, useState } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import {
  buildStandardColumns,
  createCellExportOptions,
  DemandTableProps,
  DemandTableRow,
  digestData,
} from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandTableHelpers';
import { ProductDetailsTableRow } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/ProductDetailsTableRow';
import { useDataEntriesInTables } from 'components/table/useDataEntriesInTables';

interface DemandStyleTableRow extends DemandTableRow {
  styBrandColor: string;
  artImageUrl: string;
}

export function StyleTable({ merchantData, filters }: DemandTableProps) {
  const intl = useIntl();

  const { getMeasureByMetric, getAttributes } = useModelService();
  const [styBrandColor, artImageUrl] = getAttributes(['styBrandColor', 'artImageUrl']);
  const ordGmvVal = getMeasureByMetric('ordGmvVal', 'OD');
  const ordQty = getMeasureByMetric('ordQty', 'OD');

  const defaultColumns = buildStandardColumns(ordQty, intl);

  const { createAttributeColumn } = useDataEntriesInTables();

  const buildColumns = () => [createAttributeColumn(artImageUrl), createAttributeColumn(styBrandColor)];

  const cols: ColumnDef<DemandStyleTableRow>[] = (buildColumns() as ColumnDef<ProductDetailsTableRow>[]).concat(
    defaultColumns as ColumnDef<ProductDetailsTableRow>[]
  ) as ColumnDef<DemandStyleTableRow>[];

  const { data } = useEntriesDemandReportData(styBrandColor.code, filters);

  const { currentDataVersion } = useContext(TenantConfigContext);

  const [tableRows, setTableRows] = useState<DemandStyleTableRow[]>([]);

  useEffect(() => {
    if (currentDataVersion) {
      digestData(
        merchantData,
        data,
        styBrandColor,
        ordGmvVal,
        ordQty,
        entry => ({
          styBrandColor: styBrandColor.getValue(entry.attributes),
          artImageUrl: artImageUrl.getValue(entry.attributes),
        }),
        setTableRows
      );
    }
  }, [data, merchantData, currentDataVersion]);

  return (
    <DataTableContextProvider>
      <DataTable
        rows={tableRows}
        columns={cols}
        titleId="insights.style"
        defaultSortingState={[{ id: 'ordGmvVal', desc: true }]}
        exportConfigOptions={createCellExportOptions('Style')}
      />
    </DataTableContextProvider>
  );
}
